import { FC } from 'react'

import { Box, Typography, alpha } from '@mui/material'

const MOTIVATION_SCORE_MAX = 5

function formatScore(value: number): string {
  return value.toFixed(1)
}

export interface DualTrackBarProps {
  gameDesign: number
  creativeAds: number
  gameDesignColor: string
  creativeAdsColor: string
  gameDesignLabel: string
  creativeAdsLabel: string
}

/**
 * Two stacked progress tracks rendered on the same 0–5 axis so the visual
 * distance between the bar ends *is* the gap between the Game & Live Events
 * and Creative Ads signals. Used by the diagnosis cards as well as the
 * dedicated Blind Spot / Alignment dialogs.
 */
export const DualTrackBar: FC<DualTrackBarProps> = ({ gameDesign, creativeAds, gameDesignColor, creativeAdsColor, gameDesignLabel, creativeAdsLabel }) => (
  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, mt: 0.5 }}>
    <DualTrackRow label={gameDesignLabel} value={gameDesign} color={gameDesignColor} />
    <DualTrackRow label={creativeAdsLabel} value={creativeAds} color={creativeAdsColor} />
  </Box>
)

interface DualTrackRowProps {
  label: string
  value: number
  color: string
}

const DualTrackRow: FC<DualTrackRowProps> = ({ label, value, color }) => {
  const fillPct = Math.max(0, Math.min(1, value / MOTIVATION_SCORE_MAX)) * 100
  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: '110px 1fr 44px', alignItems: 'center', gap: 1, minWidth: 0 }}>
      <Typography
        variant="caption"
        sx={{
          fontSize: '0.62rem',
          fontWeight: 700,
          letterSpacing: '0.05em',
          textTransform: 'uppercase',
          color,
          lineHeight: 1.2,
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
      >
        {label}
      </Typography>
      <Box
        sx={{
          height: 8,
          borderRadius: 4,
          backgroundColor: alpha(color, 0.16),
          overflow: 'hidden',
        }}
        role="presentation"
        aria-hidden
      >
        <Box
          sx={{
            height: '100%',
            width: `${fillPct}%`,
            backgroundColor: color,
            borderRadius: 4,
            transition: 'width 0.3s',
          }}
        />
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.25, justifyContent: 'flex-end' }}>
        <Typography sx={{ fontSize: '0.85rem', fontWeight: 700, color: 'text.primary', fontVariantNumeric: 'tabular-nums', lineHeight: 1.2 }}>
          {formatScore(value)}
        </Typography>
        <Typography sx={{ fontSize: '0.62rem', color: 'text.secondary', fontVariantNumeric: 'tabular-nums' }}>/ {MOTIVATION_SCORE_MAX}</Typography>
      </Box>
    </Box>
  )
}
